/**
 * ListLayout - Shell layout for list pages (Users, Deposits, Withdrawals)
 *
 * Layout tier: Orchestrates core + composed components.
 *
 * Uses existing components:
 * - SearchInput (core) - for search functionality
 * - FilterButton (core) - for filter trigger
 * - SortButton (composed) - for sort with bottom sheet
 * - MenuButton (composed) - for navigation menu
 * - InfoCardList (composed) - page passes as children
 *
 * Features:
 * - Title + count header
 * - Loading skeleton state
 * - Empty state with customizable content
 */

import { cn } from '@/lib/utils';
import { SearchInput } from '../core/SearchInput';
import { FilterButton } from '../core/FilterButton';
import type { SortOption } from '../composed/SortButton';
import { SortButton } from '../composed/SortButton';
import type { MenuOption } from '../composed/MenuButton';
import { MenuButton } from '../composed/MenuButton';

export interface ListLayoutProps {
  /** Page title */
  title: string;
  /** Item count */
  count: number;
  /** Count label (e.g., "users", "deposits") */
  countLabel?: string;

  /** Search query - controlled by page */
  searchQuery: string;
  /** Search change handler */
  onSearchChange: (query: string) => void;
  /** Search placeholder */
  searchPlaceholder?: string;

  /** Filter button click (opens filter panel) */
  onFilterClick: () => void;
  /** Whether filter panel is open */
  isFilterActive?: boolean;
  /** Number of active filters (shows badge) */
  activeFilterCount?: number;

  /** Sort options for SortButton */
  sortOptions: SortOption[];
  /** Current sort value */
  sortValue: string;
  /** Sort change handler */
  onSortChange: (value: string) => void;

  /** Menu options for MenuButton */
  menuOptions: MenuOption[];
  /** Current page/menu value */
  menuValue?: string;
  /** Menu selection handler */
  onMenuSelect: (value: string) => void;

  /** List content (InfoCardList) */
  children: React.ReactNode;

  /** Filter dropdowns - rendered between toolbar and header */
  filterContent?: React.ReactNode;

  /** Loading state - shows skeleton */
  isLoading?: boolean;
  /** Number of skeleton cards to show */
  loadingCount?: number;

  /** Empty state - no items */
  isEmpty?: boolean;
  /** Custom empty state content */
  emptyContent?: React.ReactNode;

  /** Optional className for content area */
  className?: string;

  /** Ref for the scroll container (for infinite scroll) */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

export function ListLayout({
  title,
  count,
  countLabel,
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search...',
  onFilterClick,
  isFilterActive = false,
  activeFilterCount = 0,
  sortOptions,
  sortValue,
  onSortChange,
  menuOptions,
  menuValue,
  onMenuSelect,
  children,
  filterContent,
  isLoading = false,
  loadingCount = 5,
  isEmpty = false,
  emptyContent,
  className,
  scrollContainerRef,
}: ListLayoutProps) {
  return (
    <div className="flex flex-col h-full bg-slate-100">
      {/* Toolbar - uses core/composed components */}
      <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          {/* Search - from core */}
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            expandedWidth={180}
          />

          {/* Filter - from core */}
          <FilterButton
            onClick={onFilterClick}
            isActive={isFilterActive}
            activeCount={activeFilterCount}
          />

          {/* Spacer */}
          <div className="flex-1" />

          {/* Sort - from composed */}
          <SortButton
            options={sortOptions}
            value={sortValue}
            onChange={onSortChange}
          />

          {/* Menu - from composed */}
          <MenuButton
            options={menuOptions}
            value={menuValue}
            onSelect={onMenuSelect}
          />
        </div>
      </div>

      {/* Filter Dropdowns - between toolbar and header */}
      {filterContent && (
        <div className="flex-shrink-0 px-4 pt-3">
          {filterContent}
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2">
        <h1 className="text-xl font-bold text-slate-800">{title}</h1>
        <p className="text-sm text-slate-500">
          {count} {countLabel || 'items'}
        </p>
      </div>

      {/* Content Area */}
      <div
        ref={scrollContainerRef}
        className={cn('flex-1 overflow-y-auto px-4 pb-4', className)}
      >
        {isLoading ? (
          /* Loading Skeleton */
          <div className="flex flex-col gap-3">
            {Array.from({ length: loadingCount }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'h-14 rounded-full',
                  'bg-white',
                  'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
                  'animate-pulse',
                )}
              >
                <div className="flex items-center h-full px-4 gap-3">
                  <div className="w-32 h-4 bg-slate-200 rounded" />
                  <div className="flex-1" />
                  <div className="w-16 h-4 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center h-full py-12">
            {emptyContent || (
              <p className="text-slate-400 text-sm">No items found</p>
            )}
          </div>
        ) : (
          /* Content - InfoCardList passed as children */
          children
        )}
      </div>
    </div>
  );
}
